/**
 * Image utility functions for handling backend image URLs
 */

/**
 * Get the full image URL from backend path
 * @param imagePath - The image path from backend (e.g., "uploads/products/image.webp")
 * @returns Full URL to the image
 */
export function getImageUrl(imagePath: string): string {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
  return `${baseUrl}/storage/${imagePath}`;
}

/**
 * Get the thumbnail version of an image
 * @param imagePath - The original image path from backend
 * @returns Full URL to the thumbnail image
 */
export function getThumbnailUrl(imagePath: string): string {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
  
  // Extract the filename from the path
  const pathParts = imagePath.split('/');
  const filename = pathParts[pathParts.length - 1];
  const directory = pathParts.slice(0, -1).join('/');
  
  // Add thumb_ prefix to filename
  const thumbnailFilename = `thumb_${filename}`;
  const thumbnailPath = directory ? `${directory}/${thumbnailFilename}` : thumbnailFilename;
  
  return `${baseUrl}/storage/${thumbnailPath}`;
}

/**
 * Get both original and thumbnail URLs for an image
 * @param imagePath - The image path from backend
 * @returns Object with original and thumbnail URLs
 */
export function getImageUrls(imagePath: string) {
  return {
    original: getImageUrl(imagePath),
    thumbnail: getThumbnailUrl(imagePath),
  };
}

/**
 * Check if an image URL is valid (not empty or null)
 * @param imageUrl - The image URL to check
 * @returns Boolean indicating if the URL is valid
 */
export function isValidImageUrl(imageUrl?: string | null): boolean {
  return Boolean(imageUrl && imageUrl.trim().length > 0);
}

/**
 * Get a fallback image URL for a specific type
 * @param type - The type of fallback image needed
 * @returns Fallback image URL
 */
export function getFallbackImageUrl(type: 'product' | 'category' | 'user' = 'product'): string {
  const fallbackImages = {
    product: 'https://images.pexels.com/photos/325153/pexels-photo-325153.jpeg?auto=compress&cs=tinysrgb&w=800',
    category: 'https://images.pexels.com/photos/325153/pexels-photo-325153.jpeg?auto=compress&cs=tinysrgb&w=400',
    user: 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=150',
  };
  
  return fallbackImages[type];
}